import React, { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Landmark, CheckCircle2 } from 'lucide-react';
import { usePolicyMakerNav } from '../context/PolicyMakerNavContext';
import { ClickInfoTooltip } from '../components/ClickInfoTooltip';
import {
  BRIEF_FORMATS,
  BRIEFING_TRIGGERS,
  FOCUS_MINERALS,
  PLANNING_HORIZONS,
  PRELOADED_BLOCK_AUCTIONS,
  PRELOADED_MOUS,
  PRIMARY_MANDATES,
  RESPONSIBLE_SECTORS,
  initialPolicyMakerFormState,
  loadPolicyMakerProfile,
  savePolicyMakerProfile,
} from '../utils/policyMakerProfileStorage';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#03396c] focus:border-transparent text-sm';

const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

const checkboxClass =
  'rounded border-gray-300 text-[#03396c] focus:ring-[#03396c] dark:border-gray-600';

function SectionHeader({ letter, title, subtitle }) {
  const HELP = {
    'Who You Are': {
      meaning: 'Your ministry identity and policy role.',
      useful: 'Lets the system tailor the briefing tone and decision framing to your mandate.',
    },
    'What You Want to Track': {
      meaning: 'The minerals, sectors, and time horizon you care about most.',
      useful: 'Ensures alerts map to your priorities instead of generating noise outside your scope.',
    },
    'What India Has Already Done': {
      meaning: 'A record of MoUs and agreements you can activate for follow-up.',
      useful: 'Helps you quickly convert public commitments into concrete next actions.',
    },
    'How You Want to Be Alerted': {
      meaning: 'How and when briefings should be triggered for you.',
      useful: 'So you receive the right format at the right moment to respond early.',
    },
  };

  const help = HELP[title] || {
    meaning: 'This section captures a decision-relevant input for your policy briefings.',
    useful: 'Use it to align alerts with your mandate and operational workflow.',
  };

  return (
    <div className="flex items-start gap-3 pt-2 pb-4 border-b border-gray-200 dark:border-gray-700 mb-6">
      <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#03396c] dark:bg-blue-800 text-white text-sm font-bold flex items-center justify-center">
        {letter}
      </span>
      <div>
        <div className="flex items-start gap-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          <ClickInfoTooltip label={title ? `About ${title}` : 'About this section'}>
            <>
              <div className="font-semibold mb-0.5">What it means</div>
              <div className="mb-1.5">{help.meaning}</div>
              <div className="font-semibold mb-0.5">Why it is useful</div>
              <div>{help.useful}</div>
            </>
          </ClickInfoTooltip>
        </div>
        {subtitle && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children, className = '' }) {
  return (
    <div className={className}>
      <label className={labelClass}>
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function toggleListValue(list, value, exclusiveAll = false) {
  const set = new Set(list);
  if (exclusiveAll && value === 'All') {
    if (set.has('All')) return [];
    return ['All'];
  }
  if (exclusiveAll && set.has('All')) set.delete('All');
  if (set.has(value)) set.delete(value);
  else set.add(value);
  return [...set];
}

const PolicyMakerPage = () => {
  const [searchParams] = useSearchParams();
  const forceSetup = searchParams.get('setup') === '1';

  const [view, setView] = useState('loading');
  const [form, setForm] = useState(initialPolicyMakerFormState);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const { setWorkspaceNav } = usePolicyMakerNav() || {};

  useEffect(() => {
    const saved = loadPolicyMakerProfile();
    if (saved) {
      setForm(saved);
      setView(forceSetup ? 'form' : 'dashboard');
    } else {
      setView('form');
    }
  }, [forceSetup]);

  useEffect(() => {
    if (!setWorkspaceNav) return undefined;
    if (view !== 'dashboard') {
      setWorkspaceNav(null);
      return undefined;
    }
    setWorkspaceNav({
      onEditProfile: () => setView('form'),
    });
    return () => setWorkspaceNav(null);
  }, [view, setWorkspaceNav]);

  if (view === 'loading') {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }

  if (view === 'dashboard') {
    return <Navigate to="/policy-makers/alerts" replace />;
  }

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSubmitted(false);
  };

  const toggleMineral = (mineral) => {
    setForm((prev) => ({
      ...prev,
      priorityMinerals: toggleListValue(prev.priorityMinerals, mineral),
    }));
    setSubmitted(false);
  };

  const toggleSector = (sector) => {
    setForm((prev) => ({
      ...prev,
      responsibleSectors: toggleListValue(prev.responsibleSectors, sector, true),
    }));
    setSubmitted(false);
  };

  const toggleTrigger = (trigger) => {
    setForm((prev) => ({
      ...prev,
      briefingTriggers: toggleListValue(prev.briefingTriggers, trigger, true),
    }));
    setSubmitted(false);
  };

  const updateMou = (id, field, value) => {
    setForm((prev) => ({
      ...prev,
      mouSelections: prev.mouSelections.map((row) =>
        row.id === id ? { ...row, [field]: value } : row,
      ),
    }));
    setSubmitted(false);
  };

  const updateAuction = (id, field, value) => {
    setForm((prev) => ({
      ...prev,
      auctionSelections: prev.auctionSelections.map((row) =>
        row.id === id ? { ...row, [field]: value } : row,
      ),
    }));
    setSubmitted(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!form.ministryDepartmentName.trim()) {
      setError('Ministry / Department name is required.');
      return;
    }
    if (!form.policyRole.trim()) {
      setError('Your role in critical mineral policy is required.');
      return;
    }
    if (!form.primaryMandate) {
      setError('Please select your primary mandate.');
      return;
    }
    if (!form.priorityMinerals.length) {
      setError('Select at least one priority mineral (1 to 5).');
      return;
    }
    if (!form.responsibleSectors.length) {
      setError('Select at least one sector you are responsible for.');
      return;
    }
    if (!form.planningHorizon) {
      setError('Please select your planning horizon.');
      return;
    }
    if (!form.briefFormat) {
      setError('Please select a brief format and frequency.');
      return;
    }
    if (!form.briefingTriggers.length) {
      setError('Select at least one briefing trigger.');
      return;
    }

    savePolicyMakerProfile(form);
    setSubmitted(true);
    setView('dashboard');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200">
      <div className="container mx-auto px-4 sm:px-6 py-8 md:py-12 max-w-3xl">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 rounded-xl bg-[#03396c]/10 dark:bg-blue-900/30">
            <Landmark className="h-8 w-8 text-[#03396c] dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Policy Maker Profile</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm md:text-base">
              Configure your ministry mandate, tracking priorities, active agreements, and
              briefing preferences — similar to the corporate onboarding flow.
            </p>
          </div>
        </div>

        {submitted && (
          <div className="mb-6 flex items-start gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">Profile saved. Opening policy workspace…</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-10">
          {/* A. WHO YOU ARE */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 md:p-8">
            <SectionHeader
              letter="A"
              title="Who You Are"
              subtitle="Ministry identity and policy mandate"
            />
            <div className="space-y-5">
              <Field label="Ministry / Department name" required>
                <input
                  type="text"
                  value={form.ministryDepartmentName}
                  onChange={(e) => update('ministryDepartmentName', e.target.value)}
                  className={inputClass}
                  placeholder="e.g. Ministry of Mines, NITI Aayog, PMO"
                />
              </Field>
              <Field label="Your role in critical mineral policy" required>
                <input
                  type="text"
                  value={form.policyRole}
                  onChange={(e) => update('policyRole', e.target.value)}
                  className={inputClass}
                  placeholder="e.g. Joint Secretary, Policy Advisor, Director"
                />
              </Field>
              <Field label="Primary mandate" required>
                <select
                  value={form.primaryMandate}
                  onChange={(e) => update('primaryMandate', e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select mandate</option>
                  {PRIMARY_MANDATES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </section>

          {/* B. WHAT YOU WANT TO TRACK */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 md:p-8">
            <SectionHeader
              letter="B"
              title="What You Want to Track"
              subtitle="Minerals, sectors, and planning horizon"
            />
            <div className="space-y-6">
              <Field label="Which of the 5 minerals are your priority right now" required>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Lithium · Cobalt · Nickel · Graphite · Rare Earth — pick 1 to 5
                </p>
                <div className="flex flex-wrap gap-3">
                  {FOCUS_MINERALS.map((m) => (
                    <label
                      key={m}
                      className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={form.priorityMinerals.includes(m)}
                        onChange={() => toggleMineral(m)}
                        className={checkboxClass}
                      />
                      {m}
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="Which sectors are you responsible for" required>
                <div className="flex flex-wrap gap-3">
                  {RESPONSIBLE_SECTORS.map((s) => (
                    <label
                      key={s}
                      className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={form.responsibleSectors.includes(s)}
                        onChange={() => toggleSector(s)}
                        className={checkboxClass}
                      />
                      {s}
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="Your planning horizon" required>
                <div className="flex flex-wrap gap-4">
                  {PLANNING_HORIZONS.map((y) => (
                    <label
                      key={y}
                      className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="planningHorizon"
                        checked={form.planningHorizon === y}
                        onChange={() => update('planningHorizon', y)}
                        className="text-[#03396c] focus:ring-[#03396c]"
                      />
                      {y}
                    </label>
                  ))}
                </div>
              </Field>
            </div>
          </section>

          {/* C. WHAT INDIA HAS ALREADY DONE */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 md:p-8">
            <SectionHeader
              letter="C"
              title="What India Has Already Done"
              subtitle="Confirm public-record MoUs and flag domestic auction oversight"
            />
            <div className="space-y-8">
              <div>
                <p className={labelClass}>
                  Which active MoUs or KABIL agreements should we pre-load
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Pre-populated from public records — confirm each agreement or add notes.
                  Include any non-public deals below.
                </p>
                <div className="space-y-3">
                  {PRELOADED_MOUS.map((mou) => {
                    const sel = form.mouSelections.find((s) => s.id === mou.id);
                    if (!sel) return null;
                    return (
                      <div
                        key={mou.id}
                        className="p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                      >
                        <label className="inline-flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={sel.confirmed}
                            onChange={(e) => updateMou(mou.id, 'confirmed', e.target.checked)}
                            className={`${checkboxClass} mt-1`}
                          />
                          <span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white block">
                              {mou.label}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {mou.country} · {mou.minerals.join(', ')} · {mou.source}
                            </span>
                          </span>
                        </label>
                        <input
                          type="text"
                          value={sel.notes}
                          onChange={(e) => updateMou(mou.id, 'notes', e.target.value)}
                          className={`${inputClass} mt-3`}
                          placeholder="Optional note (e.g. cabinet approval pending)"
                        />
                      </div>
                    );
                  })}
                </div>
                <Field label="Additional MoUs or agreements (non-public)" className="mt-4">
                  <textarea
                    value={form.additionalMous}
                    onChange={(e) => update('additionalMous', e.target.value)}
                    rows={3}
                    className={inputClass}
                    placeholder="e.g. Confidential JV under negotiation with Country X…"
                  />
                </Field>
              </div>

              <div>
                <p className={labelClass}>
                  Which domestic block auctions are within your oversight
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Pre-loaded scheduled auctions from public announcements — flag blocks in your
                  oversight or not yet public.
                </p>
                <div className="space-y-3">
                  {PRELOADED_BLOCK_AUCTIONS.map((auction) => {
                    const sel = form.auctionSelections.find((s) => s.id === auction.id);
                    if (!sel) return null;
                    return (
                      <div
                        key={auction.id}
                        className="p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                      >
                        <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                          {auction.label}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                          {auction.state} · {auction.mineral} · {auction.source}
                        </p>
                        <div className="flex flex-wrap gap-4 mb-2">
                          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={sel.inOversight}
                              onChange={(e) =>
                                updateAuction(auction.id, 'inOversight', e.target.checked)
                              }
                              className={checkboxClass}
                            />
                            Within my oversight
                          </label>
                          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={sel.notYetPublic}
                              onChange={(e) =>
                                updateAuction(auction.id, 'notYetPublic', e.target.checked)
                              }
                              className={checkboxClass}
                            />
                            Not yet public
                          </label>
                        </div>
                        <input
                          type="text"
                          value={sel.notes}
                          onChange={(e) => updateAuction(auction.id, 'notes', e.target.value)}
                          className={inputClass}
                          placeholder="Optional note"
                        />
                      </div>
                    );
                  })}
                </div>
                <Field label="Additional blocks not yet in public domain" className="mt-4">
                  <textarea
                    value={form.additionalAuctions}
                    onChange={(e) => update('additionalAuctions', e.target.value)}
                    rows={3}
                    className={inputClass}
                    placeholder="e.g. Upcoming graphite tranche in Andhra Pradesh…"
                  />
                </Field>
              </div>
            </div>
          </section>

          {/* D. HOW YOU WANT TO BE ALERTED */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 md:p-8">
            <SectionHeader
              letter="D"
              title="How You Want to Be Alerted"
              subtitle="Briefing triggers and delivery format"
            />
            <div className="space-y-6">
              <Field label="What event triggers a briefing for you" required>
                <div className="flex flex-col gap-2">
                  {BRIEFING_TRIGGERS.map((t) => (
                    <label
                      key={t}
                      className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={form.briefingTriggers.includes(t)}
                        onChange={() => toggleTrigger(t)}
                        className={checkboxClass}
                      />
                      {t}
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="Brief format and frequency" required>
                <select
                  value={form.briefFormat}
                  onChange={(e) => update('briefFormat', e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select format</option>
                  {BRIEF_FORMATS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="submit"
              className="flex-1 bg-[#03396c] hover:bg-[#022a4d] text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Save & open policy workspace
            </button>
            <button
              type="button"
              onClick={() => {
                setForm(initialPolicyMakerFormState());
                setSubmitted(false);
                setError('');
              }}
              className="px-6 py-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 text-sm font-medium"
            >
              Reset form
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PolicyMakerPage;
